<template>
  <form
      @submit.prevent="submitInvoice"
      autocomplete="off"
      class="w-full max-h-screen bg-background flex flex-col relative"
      :class="{ 'overflow-y-hidden': isBusy }"
  >
    <div
        v-show="isBusy"
        class="absolute inset-0 bg-faint-white flex items-center justify-center z-10"
    >
      <spinner :color="'text-black'" :size="10"/>
    </div>
    <page-header :title="pageTitle" :info="getInvoiceNo" :enableLogicalBack="true" back-to="invoice">

      <SplitButton
          v-if="invoice_id && invoiceStatusItems.length > 0"
          label="Set Status"
          :model="invoiceStatusItems"
          class="status-split-button mb-2"
          style="height: 35px;top:4px"
      ></SplitButton>

      <!-- Current Step Buttons -->
      <div v-for="button in currentStepButtons" :key="button.label" class="inline-block">
        <AppButton
            :color="button.color"
            :label="button.label"
            class="ml-2"
            @click="button.action"
            :icon="!!button.icon"
        >
          <template v-slot:icon v-if="button.icon">
            <Icon :iconType="button.icon" class="mr-2"/>
          </template>
        </AppButton>
      </div>

    </page-header>


    <main class="pr-6 pl-6   flex-1 overflow-y-scroll">
      <div class="flex gap-4 mt-6">
        <!-- Main Column 1 -->
        <div class="w-2/12 bg-white p-4 rounded flex flex-col justify-between">
          <div>
            <div class="flex items-center mb-2">
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
                Sold To <span class="text-red-400">*</span>
              </h3>
              <hr class="flex-1 border-primary ml-4" />
            </div>
            <div>
              <ContactAutocomplete
                  class="w-full"
                  v-model="sold_to"
                  @contactSelected="handleSoldToSelected"
                  @contactCleared="handleSoldToCleared"
                  :forceSelection="true"
                  :disabled="isInvoiceLocked"
              />
            </div>
            <div class="w-full flex">
              <ContactInfoCard :contact="sold_to"></ContactInfoCard>
            </div>
          </div>
          <div class="flex justify-between items-end mt-4">
            <!-- Open credit belongs to the Sold To customer's account, so the balance and the
                 link to add more sit here, as plain text rather than a second button -->
            <a
                v-if="canCreateOpenCredit"
                href="#"
                @click.prevent="newOpenCredit"
                class="text-orange-500 font-semibold text-sm hover:underline"
            >
              + New open credit
            </a>
            <span v-else></span>
            <AppOutlinedButton
                v-if="!isInvoiceLocked"
                label="Add New"
                color="indigo"
                @click="addNewContact"
                :icon="true"
            >
              <template v-slot:icon>
                <Icon iconType="add" class="mr-2" />
              </template>
            </AppOutlinedButton>
          </div>
        </div>

        <!-- Main Column 2 -->
        <div class="w-2/12 bg-white p-4 rounded flex flex-col justify-between">
          <div>
            <div class="flex items-center mb-2">
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
                Invoice To <span class="text-red-400">*</span>
              </h3>
              <hr class="flex-1 border-primary ml-4" />
            </div>
            <div>
              <ContactAutocomplete
                  class="w-full"
                  v-model="invoice_to"
                  @contactSelected="handleInvoiceToSelected"
                  @contactCleared="handleInvoiceToCleared"
                  :forceSelection="true"
                  :disabled="isInvoiceLocked"
              />
            </div>
            <div class="w-full flex">
              <ContactInfoCard :contact="invoice_to"></ContactInfoCard>
            </div>
          </div>
          <!-- Status and credit rows sit together at the bottom of the tile, so the spare height
               goes above them rather than being spread between every row -->
          <div class="mt-4">
            <div class="flex justify-between items-center">
              <span class="uppercase text-sm font-semibold">Status:</span>
              <span
                  :class="getStatusClass(invoice.status)"
                  class="px-3 py-1 text-sm font-semibold rounded"
              >
        {{ (invoice.status || 'N/A').toUpperCase() }}
      </span>
            </div>
            <template v-if="creditNotes.length > 0">
              <router-link
                  v-for="note in creditNotes"
                  :key="note.credit_id"
                  :to="{ name: 'credit-edit', params: { credit_id: note.credit_id } }"
                  class="flex justify-between items-center mt-1 font-semibold text-sm hover:underline"
                  :class="note.status === 'void' ? 'text-gray-400 line-through' : 'text-orange-500'"
              >
                <span class="uppercase">{{ note.credit_no }} ({{ (note.status || '').toUpperCase() }})</span>
                <span>{{ formatMoney(note.amount) }} &rarr;</span>
              </router-link>
            </template>
            <router-link
                v-else-if="showSingleCreditLink"
                :to="{ name: 'credit-edit', params: { credit_id: invoice.credit_id } }"
                class="flex justify-between items-center mt-1 text-orange-500 font-semibold text-sm hover:underline"
            >
              <span class="uppercase">Credited</span>
              <span>View Credit Note &rarr;</span>
            </router-link>
            <div
                v-if="invoice_id && sold_to && sold_to.contact_id"
                class="flex justify-between items-center mt-1 font-semibold text-sm"
                :class="availableCredits.length > 0 ? 'text-green-700' : 'text-gray-500'"
            >
              <span class="uppercase">Open Credit Available</span>
              <span>{{ formatMoney(availableCreditTotal) }}</span>
            </div>
          </div>
        </div>

        <!-- Main Column 3 -->
        <div class="w-2/12 bg-white p-4 rounded flex flex-col justify-between">
          <div>
            <div class="flex items-center mb-2">
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
                Delivery To <span class="text-red-400">*</span>
              </h3>
              <hr class="flex-1 border-primary ml-4" />
            </div>
            <div>
              <ContactAutocomplete
                  class="w-full"
                  v-model="delivery_to"
                  @contactSelected="handleDeliveryToSelected"
                  @contactCleared="handleDeliveryToCleared"
                  :forceSelection="true"
                  :disabled="isInvoiceLocked"
              />
            </div>
            <div class="w-full flex">
              <ContactInfoCard :contact="delivery_to"></ContactInfoCard>
            </div>
          </div>
        </div>

        <!-- Main Column 4 -->
        <div class="w-6/12 bg-white p-4 rounded flex flex-col">
          <div class="flex items-center mb-2">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Set Details
            </h3>
            <hr class="flex-1 border-primary ml-4" />
          </div>
          <div class="grid grid-cols-3 gap-2">
            <!-- First Main Column (66%) -->
            <div class="col-span-2 grid grid-cols-2 gap-2">
              <!-- First Sub-Column -->
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Invoice Date <span class="text-red-400">*</span></label>
                <Calendar
                    v-model="invoice.invoice_date"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                    :disabled="isInvoiceLocked"
                />
              </div>
              <!-- Second Sub-Column -->
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Payment Due Date <span class="text-red-400">*</span></label>
                <Calendar
                    v-model="invoice.payment_due_date"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                    :disabled="isInvoiceLocked"
                />
              </div>
              <!-- Invoice Notes -->
              <div class="col-span-2 flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Invoice Notes</label>
                <textarea
                    v-model="invoice.notes"
                    :disabled="isInvoiceLocked"
                    class="border rounded bg-gray-100 border-gray-400 hover:border-primary focus:border-primary py-3 px-4 focus:shadow w-full"
                    placeholder="Note..."
                    rows="3"
                ></textarea>
              </div>
              <!-- Internal Notes -->
              <div class="col-span-2 flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Internal Notes</label>
                <textarea
                    v-model="invoice.internal_notes"
                    class="border rounded bg-gray-100 border-gray-400 hover:border-primary focus:border-primary py-3 px-4 focus:shadow w-full"
                    placeholder="Note..."
                    rows="3"
                ></textarea>
              </div>
            </div>
            <!-- Second Main Column (33%) -->
            <div class="col-span-1 flex flex-col gap-2">
              <!-- Supplier Reference -->
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Customer PO</label>
                <input
                    v-model="invoice.customer_po"
                    :disabled="isInvoiceLocked"
                    class="bge-input bge-input-spacing rounded"
                    type="text"
                />
              </div>
              <!-- Order Currency -->
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Invoice Type <span class="text-red-400">*</span></label>
                <select v-model="invoice.invoice_type" :disabled="isInvoiceLocked || isSavedWarrantyInvoice" class="bge-input bge-input-spacing rounded">
                  <option value="SPARE PARTS">SPARE PARTS</option>
                  <option value="INT JOBSHEET">INT JOBSHEET</option>
                  <option value="EXT JOBSHEET">EXT JOBSHEET</option>
                  <option value="EQUIPMENT">EQUIPMENT</option>
                  <option value="WARRANTY" :disabled="invoiceKindLocked && !isSavedWarrantyInvoice">WARRANTY</option>
                </select>
                <span v-if="isSavedWarrantyInvoice" class="text-xs text-orange-700 mt-1">
                  A warranty invoice can't be changed to a normal invoice.
                </span>
                <span v-else-if="!invoiceKindLocked && isWarrantyType && !invoice.is_pro_forma" class="text-xs text-orange-700 mt-1">
                  A warranty invoice is numbered WAR-... and can't be changed to a normal invoice once it is saved.
                </span>
              </div>

              <!-- Pro Farma Currency -->
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">PRO-FORMA <span class="text-red-400">*</span></label>
                <select v-model="proFormaDisplay" :disabled="isInvoiceLocked || invoiceKindLocked" class="bge-input bge-input-spacing rounded">
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </select>
                <span v-if="invoiceKindLocked && invoice.is_pro_forma" class="text-xs text-orange-700 mt-1">
                  To issue this as a real invoice use Convert to Invoice.
                </span>
                <span v-else-if="!invoiceKindLocked && invoice.is_pro_forma" class="text-xs text-orange-700 mt-1">
                  A pro-forma is numbered PF-... and
                  <template v-if="isWarrantyType">will be numbered WAR-... when it is converted to a real invoice.</template>
                  <template v-else>can be converted to a real invoice later.</template>
                </span>
                <span v-if="invoice.pro_forma_no" class="text-xs text-secondary mt-1">
                  Converted from pro-forma {{ invoice.pro_forma_no }}.
                </span>
              </div>

            </div>
          </div>
        </div>
      </div>


      <!-- Select Products -->
      <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-2">
            <h3
                class="uppercase text-lg font-semibold text-primary tracking-wide"
            >
              Select Products
            </h3>
            <hr class="flex-1 border-primary ml-4"/>

            <AppButton v-if="(invoice.status === 'pending' || invoice.status === 'under query') && !invoice.sales_order_no"
                       color="indigo"
                       label="Add Product"
                       @click="showAddProduct"
                       :icon=true
                       class="ml-4">
              <template v-slot:icon>
                <Icon iconType="add" class="mr-2"/>
              </template>
            </AppButton>


          </div>
          <OrderItems :invoice_id="invoice_id" ref="orderItemRef" @changed="handleInvoiceItemChanged"
                      :initialProducts="invoice.invoice_items" :invoice="invoice"></OrderItems>

          <p v-if="invoice.credit_applications && invoice.credit_applications.length"
             class="uppercase text-xs font-semibold text-orange-500 mt-2">
            Credit note lines above are for internal use only &mdash; not shown on the printed invoice
          </p>
        </div>
      </div>


      <!--Order Cost -->
      <div class="w-full bg-white mt-6  p-5 pt-1 rounded shadow-md relative mb-6">
        <div class="mt-6">
          <ProductSummary
              v-if="invoice"
              :disabled="isInvoiceLocked"
              :products="getOrderItems()"
              :initialDuty="invoice.duty"
              :initialShippingCost="invoice.shipping_cost"
              :initialShippingTaxCode="invoice.shipping_tax_code"
              :initialShippingTaxRate="invoice.shipping_tax_rate"
              @update-duty="updateInvoiceValue('duty', $event)"
              @update-shipping-cost="updateInvoiceValue('shipping_cost', $event)"
              @update-shipping-tax-code="updateInvoiceValue('shipping_tax_code', $event)"
              @update-shipping-tax-rate="updateInvoiceValue('shipping_tax_rate', $event)"
          />


        </div>
      </div>

    </main>

    <portal to="overlay-outlet">
      <PDFViewer :showing="pdfViewerData.isShowingViewer" :pdfBlob="pdfViewerData.pdfData"
                 :documentName="pdfViewerData.documentName" @close="closeViewer"/>
    </portal>

    <Confirmation
        ref="unmarkPaidConfirm"
        title="Unmark As Paid"
        message="This will revert the invoice back to PENDING status. Please provide a reason for un-marking this invoice as paid."
        :requireReason="true"
        reasonPlaceholder="Reason for reverting to unpaid..."
    />

    <portal to="overlay-outlet">
      <panel
          :showing="addProductPanelOpen"
          @close="addProductPanelOpen = false"
          title="Add Product"
      >
        <AddProduct
            @complete="applyProductDetails"
        />
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="sendEmailPanelOpen"
          @close="sendEmailPanelOpen = false"
          title="Send Email"
      >
        <SendEmail v-if="invoice"
                   :invoiceId="invoice.invoice_id"
                   :invoiceNumber="invoice.invoice_no"
                   :customer="invoice_to"
                   :invoice="invoice"/>

      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="applyCreditPanelOpen"
          @close="applyCreditPanelOpen = false"
          title="Apply Credit"
      >
        <div class="flex flex-col gap-4">
          <div class="flex flex-col">
            <label class="uppercase font-semibold text-sm mb-1">Credit</label>
            <select v-model="selectedCreditId" class="bge-input bge-input-spacing rounded">
              <option v-for="credit in availableCredits" :key="credit.credit_id" :value="credit.credit_id">
                {{ credit.credit_no }} &mdash; {{ formatMoney(credit.remaining_balance) }} available
              </option>
            </select>
          </div>

          <div v-if="selectedCredit" class="flex flex-col">
            <label class="uppercase font-semibold text-sm mb-1">Amount To Apply (max {{ formatMoney(maxApplyAmount) }})</label>
            <InputNumber v-model="applyAmount" mode="currency" currency="GBP" locale="en-GB" :max="maxApplyAmount" :min="0"/>
          </div>

          <AppButton color="blue" label="Apply Credit" :icon="true" @click="applyCredit">
            <template v-slot:icon>
              <Icon iconType="file-invoice-dollar" class="mr-2"/>
            </template>
          </AppButton>
        </div>
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="additionalCreditPanelOpen"
          @close="additionalCreditPanelOpen = false"
          title="Create Another Credit Note"
      >
        <div v-if="creditSummary" class="flex flex-col gap-4">
          <p class="text-sm text-secondary">
            This invoice has already been credited. A further credit note can only be raised for
            what has not been credited yet.
          </p>

          <div class="border rounded divide-y text-sm">
            <div class="flex justify-between p-3">
              <span>Invoice total</span>
              <span class="font-semibold">{{ formatMoney(creditSummary.invoice_total) }}</span>
            </div>
            <div class="flex justify-between p-3">
              <span>Already credited (issued credit notes)</span>
              <span class="font-semibold">{{ formatMoney(creditSummary.already_credited) }}</span>
            </div>
            <div class="flex justify-between p-3">
              <span>Still owed on this invoice</span>
              <span class="font-semibold">{{ formatMoney(creditSummary.outstanding_balance) }}</span>
            </div>
            <div class="flex justify-between p-3 bg-gray-100">
              <span class="font-semibold">Maximum for a new credit note</span>
              <span class="font-semibold">{{ formatMoney(creditSummary.max_amount) }}</span>
            </div>
          </div>

          <div v-if="creditSummary.credit_notes.length">
            <h4 class="uppercase font-semibold text-sm mb-2">Existing credit notes</h4>
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b-2 border-primary text-left">
                  <th class="py-1">Credit Note</th>
                  <th class="py-1">Status</th>
                  <th class="py-1">Date</th>
                  <th class="py-1 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="note in creditSummary.credit_notes" :key="note.credit_id" class="border-b border-gray-200">
                  <td class="py-1">
                    <router-link :to="{ name: 'credit-edit', params: { credit_id: note.credit_id } }" class="text-orange-500 hover:underline">
                      {{ note.credit_no }}
                    </router-link>
                  </td>
                  <td class="py-1 uppercase">{{ note.status }}</td>
                  <td class="py-1">{{ note.date ? $moment.unix(note.date).format("DD-MM-YYYY") : "" }}</td>
                  <td class="py-1 text-right">{{ formatMoney(note.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-if="!creditSummary.can_create" class="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3">
            {{ creditSummary.reason }}
          </p>

          <AppButton
              v-if="creditSummary.can_create"
              color="blue"
              label="Create Credit Note"
              :icon="true"
              @click="createCreditNoteFromPanel"
          >
            <template v-slot:icon>
              <Icon iconType="file-invoice-dollar" class="mr-2"/>
            </template>
          </AppButton>
          <AppButton
              v-else-if="creditSummary.blocking_credit"
              color="blue"
              :label="'Open ' + creditSummary.blocking_credit.credit_no"
              @click="openBlockingCredit"
          />
        </div>
      </panel>
    </portal>
  </form>
</template>

<script>
import _ from "lodash";
import PageHeader from "@/components/PageHeader.vue";
import Panel from "@/components/Panel.vue";
import Spinner from "@/components/Spinner.vue";
import ContactAutocomplete from '@/components/AutoComplete/ContactAutoComplete.vue';
import LoanEquipmentAutoComplete from '@/components/AutoComplete/LoanEquipmentAutoComplete.vue';
import AppButton from '@/components/Button.vue';
import AppOutlinedButton from '@/components/OutlinedButton.vue';
import Icon from '@/components/Icon.vue';
import notificationMixin from "@/mixins/notificationMixin";
import ContactInfoCard from "@/components/Contacts/InfoCard.vue";
import OrderItems from "@/views/HR/Invoice/Components/OrderItems.vue";
import AddProduct from "@/views/HR/Invoice/Components/AddProduct.vue";
import ProductSummary from "@/views/HR/Invoice/Components/ProductSummary.vue";
import SendEmail from "@/views/HR/Invoice/Components/SendEmail.vue";
import Confirmation from "@/components/Confirmation.vue";
import printJS from "print-js";

const Toggle = () => import("@/components/Toggle.vue");
const PDFViewer = () => import("@/components/PDFViewer.vue");
export default {
  name: "ManageInvoice",
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Panel,
    Spinner,
    ContactAutocomplete,
    LoanEquipmentAutoComplete,
    AppButton,
    Icon,
    Toggle,
    ContactInfoCard,
    OrderItems,
    AddProduct,
    ProductSummary,
    PDFViewer,
    SendEmail,
    AppOutlinedButton,
    Confirmation,
  },
  props: {
    invoice_id: String,
  },
  data() {
    return {
      isBusy: false,
      addProductPanelOpen: false,
      invoice: {
        invoice_date:null,
        payment_due_date:null,
        invoice_type:null,
        is_pro_forma: false,
        invoice_items: [],
        duty: 0.0,
        shipping_cost: 0.0,
        shipping_tax_code: "T1",
        shipping_tax_rate: 0.2,
        supplier_reference: '',
      },
      sold_to: null,
      invoice_to: null,
      delivery_to: null,
      products: [],
      tax_codes: [],
      pdfViewerData: {
        documentName: "",
        pdfData: null,
        isShowingViewer: false,
      },
      sendEmailPanelOpen: false,
      availableCredits: [],
      applyCreditPanelOpen: false,
      additionalCreditPanelOpen: false,
      creditSummary: null,
      creditSummaryLoaded: false,
      selectedCreditId: null,
      applyAmount: 0,
    };
  },
  async mounted() {
    this.isBusy = true;

    if (!this.invoice_id) {
      if (this.$store.state.user && this.$store.state.user.staff_id) {
        this.setDefaultCreatedBy()
      }
      this.setDefaultDates();
      await this.prefillSoldToFromQuery();
      this.isBusy = false;
      return;
    }

    if (this.invoice_id) {
      await this.loadInvoiceAndApply();
      return;
    }

    this.isBusy = false;

  },
  watch: {
    // Watch for changes in route parameters
    '$route.params.invoice_id': {
      handler: 'loadInvoiceAndApply',
      immediate: true
    },
    selectedCreditId() {
      this.applyAmount = this.maxApplyAmount;
    },
  },
  computed: {

    pageTitle() {
      return this.invoice_id ? "Update Invoice" : "Create Invoice";
    },

    getInvoiceNo() {
      return this.invoice && this.invoice.invoice_no ? this.invoice.invoice_no : "";
    },

    currentStepButtons() {
      const createCreditButton = { label: 'Create Credit', color: 'indigo', action: this.createCredit, icon: 'file-invoice-dollar', permissions: ['manage:credit'], hideIfProForma: true };
      const convertToInvoiceButton = { label: 'Convert to Invoice', color: 'indigo', action: this.convertToInvoice, icon: 'file-invoice-dollar', onlyIfProForma: true };
      const applyCreditButton = { label: 'Apply Credit', color: 'indigo', action: this.openApplyCreditPanel, icon: 'file-invoice-dollar', permissions: ['manage:credit'], requiresAvailableCredit: true };

      const stepButtons = {
        'pending': [
          { label: 'Preview Invoice', color: 'green', action: this.previewInvoice, icon: 'search' },
          { label: 'Email Invoice', color: 'green', action: this.sendEmail, icon: 'mail' },
          { label: 'Print Invoice', color: 'green', action: this.printInvoice, icon: 'print' },
          convertToInvoiceButton,
          createCreditButton,
          applyCreditButton,
          { label: 'Save Changes', color: 'blue', action: this.saveInvoice, icon: 'save' },
        ],
        'overdue': [
          { label: 'Preview Invoice', color: 'green', action: this.previewInvoice, icon: 'search' },
          { label: 'Email Invoice', color: 'green', action: this.sendEmail, icon: 'mail' },
          { label: 'Print Invoice', color: 'green', action: this.printInvoice, icon: 'print' },
          convertToInvoiceButton,
          createCreditButton,
          applyCreditButton,
          { label: 'Save Changes', color: 'blue', action: this.saveInvoice, icon: 'save' },
        ],
        'under query': [
          { label: 'Preview Invoice', color: 'green', action: this.previewInvoice, icon: 'search' },
          { label: 'Email Invoice', color: 'green', action: this.sendEmail, icon: 'mail' },
          { label: 'Print Invoice', color: 'green', action: this.printInvoice, icon: 'print' },
          convertToInvoiceButton,
          createCreditButton,
          applyCreditButton,
          { label: 'Save Changes', color: 'blue', action: this.saveInvoice, icon: 'save' },
        ],
        'cancelled': [
          { label: 'Preview Invoice', color: 'green', action: this.previewInvoice, icon: 'search' },
          { label: 'Print Invoice', color: 'green', action: this.printInvoice, icon: 'print' },
        ],
        'paid': [
          { label: 'Preview Invoice', color: 'green', action: this.previewInvoice, icon: 'search' },
          { label: 'Email Invoice', color: 'green', action: this.sendEmail, icon: 'mail' },
          { label: 'Print Invoice', color: 'green', action: this.printInvoice, icon: 'print' },
          convertToInvoiceButton,
          createCreditButton,
        ],
      };

      const buttons = stepButtons[this.invoice.status] || [
        { label: 'Save Changes', color: 'blue', action: this.saveInvoice, icon: 'save' },
      ];

      return buttons
          .filter(button => !button.permissions || this.AuthService.permissionCheck(button.permissions))
          .filter(button => !button.hideIfProForma || !this.invoice.is_pro_forma)
          .filter(button => !button.onlyIfProForma || this.invoice.is_pro_forma)
          .filter(button => !button.requiresAvailableCredit || this.availableCredits.length > 0);
    },

    maxApplyAmount() {
      if (!this.selectedCredit) {
        return 0;
      }
      // Invoices created before outstanding_balance was stored have no value for it, so fall
      // back to the total less what has been credited rather than treating it as zero.
      const stored = this.invoice.outstanding_balance;
      const outstanding = stored !== undefined && stored !== null
          ? stored
          : Math.max((this.invoice.total_value || 0) - (this.invoice.credited_amount || 0), 0);
      return Math.min(this.selectedCredit.remaining_balance, outstanding);
    },

    selectedCredit() {
      return this.availableCredits.find(c => c.credit_id === this.selectedCreditId) || null;
    },

    availableCreditTotal() {
      return this.availableCredits.reduce((sum, credit) => sum + (credit.remaining_balance || 0), 0);
    },



    proFormaDisplay: {
      get() {
        return this.invoice.is_pro_forma ? "YES" : "NO";
      },
      set(value) {
        this.invoice.is_pro_forma = value === "YES";
      },
    },

    isInvoiceLocked() {
      return this.invoice.status === "paid" || this.invoice.status === "cancelled";
    },

    // The invoice number is issued from a series that depends on whether this is a pro-forma or
    // a warranty invoice, so neither can be switched once the invoice has been saved.
    invoiceKindLocked() {
      return !!this.invoice_id;
    },

    // The plain "Credited - View Credit Note" link is for an invoice whose credit notes could not be
    // listed. It waits until the list has finished loading, so it doesn't flash up first.
    showSingleCreditLink() {
      return this.creditSummaryLoaded
          && this.creditNotes.length === 0
          && !!this.invoice.is_credit_created
          && !!this.invoice.credit_id;
    },

    // Every credit note raised against this invoice, as far as we know them
    creditNotes() {
      return this.creditSummary && this.creditSummary.credit_notes ? this.creditSummary.credit_notes : [];
    },

    isWarrantyType() {
      return ((this.invoice && this.invoice.invoice_type) || "").toUpperCase() === "WARRANTY";
    },

    // Offered once the invoice is saved and a Sold To customer is chosen, to staff who may
    // create credits. The credit goes on the Sold To customer's account, not this invoice.
    canCreateOpenCredit() {
      return !!this.invoice_id
          && !!(this.sold_to && this.sold_to.contact_id)
          && this.AuthService.permissionCheck(['manage:credit']);
    },

    isSavedWarrantyInvoice() {
      return this.invoiceKindLocked && this.isWarrantyType;
    },

    invoiceStatusItems() {
      // A cancelled invoice has no status to move to
      if (this.invoice.status === "cancelled") {
        return [];
      }

      if (this.invoice.status === "paid") {
        return [
          {
            label: "Mark as Unpaid",
            icon: "pi pi-undo",
            command: () => this.unmarkAsPaid(),
          },
        ];
      }

      const statuses = [
        { value: "pending", label: "Pending", icon: "pi pi-clock", command: () => this.updateInvoiceStatus("pending") },
        { value: "overdue", label: "Overdue", icon: "pi pi-exclamation-triangle", command: () => this.updateInvoiceStatus("overdue") },
        { value: "under query", label: "Under Query", icon: "pi pi-question-circle", command: () => this.updateInvoiceStatus("under query") },
        { value: "paid", label: "Paid", icon: "pi pi-check-circle", command: () => this.confirmMarkAsPaid() },
      ];

      return statuses.map(({ value, label, icon, command }) => ({
        label: value === this.invoice.status ? `${label}  ✓` : label,
        icon,
        command,
      }));
    },
  },
  methods: {

    getStatusClass(status) {
      if (!status) {
        return 'bg-gray-100 text-gray-500';
      }

      switch (status.toLowerCase()) {
        case 'pending':
          return 'bg-gray-200 text-gray-700';
        case 'overdue':
          return 'bg-red-200 text-red-700';
        case 'under query':
          return 'bg-yellow-200 text-yellow-700';
        case 'paid':
          return 'bg-green-200 text-green-700';
        case 'cancelled':
          return 'bg-gray-300 text-gray-600 line-through';
        default:
          return 'bg-gray-100 text-gray-500';
      }
    },

    async loadInvoiceAndApply() {
      this.isBusy = true;
      const invoice = await this.loadInvoiceByInvoiceId();
      this.invoice = await this.formatInvoiceData(invoice);

      if (this.invoice.sold_to) {
        this.availableCredits = await this.CreditService.getAvailableCreditsForContact(this.invoice.sold_to);
      }

      // Keep showing the previous list while it reloads, so the card doesn't flicker
      if (!this.invoice.is_credit_created) {
        this.creditSummary = null;
      }
      if (this.invoice.is_credit_created && this.invoice_id) {
        try {
          this.creditSummary = await this.CreditService.getInvoiceCreditSummary(this.invoice_id);
        } catch (error) {
          console.error("Couldn't load the credit notes for this invoice:", error);
        }
      }
      this.creditSummaryLoaded = true;

      this.isBusy = false;
    },

    openApplyCreditPanel() {
      this.selectedCreditId = this.availableCredits.length ? this.availableCredits[0].credit_id : null;
      this.applyAmount = this.maxApplyAmount;
      this.applyCreditPanelOpen = true;
    },

    async applyCredit() {
      if (!this.selectedCreditId || !this.applyAmount || this.applyAmount <= 0) {
        this.notifyError("Please select a credit and enter an amount to apply.");
        return;
      }

      if (this.applyAmount > this.maxApplyAmount) {
        this.notifyError("The amount cannot exceed the credit's remaining balance or the invoice's outstanding balance.");
        return;
      }

      const confirmation = await this.confirmAction({
        message: `Apply ${this.formatMoney(this.applyAmount)} from this credit to the invoice?`,
        header: "Confirm Apply Credit",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) {
        return;
      }

      this.isBusy = true;
      try {
        await this.CreditService.applyToInvoice(this.selectedCreditId, this.invoice_id, this.applyAmount);
        this.notifySuccess("Credit applied successfully");
        this.applyCreditPanelOpen = false;
        await this.loadInvoiceAndApply();
      } catch (error) {
        this.notifyError("Couldn't apply the credit");
        console.error(error);
      } finally {
        this.isBusy = false;
      }
    },

    async handleInvoiceItemChanged() {
      await this.loadInvoiceAndApply();
    },

    updateInvoiceValue(field, value) {

      this.invoice[field] = value;
    },

    sendEmail() {
      this.sendEmailPanelOpen = true;
    },

    async newOpenCredit() {
      await this.$router.push({
        name: 'credit-create',
        query: { sold_to: this.sold_to.contact_id },
      });
    },

    async addNewContact() {

      await this.$router.push({
        name: 'contacts',
      });
    },

    showAddProduct() {
      this.addProductPanelOpen = true;
    },
    async applyProductDetails(product) {
      await this.$refs.orderItemRef.addProducts([product]);
      await this.loadInvoiceAndApply();
    },

    setDefaultDates() {

      if (!this.invoice.invoice_date) {
        this.invoice.invoice_date = this.$moment().toDate();
      }
      if (!this.invoice.payment_due_date) {
        this.invoice.payment_due_date = this.$moment().add(3, 'days').toDate();
      }
    },

    setDefaultCreatedBy() {
      this.invoice.created_by = {
        name: `${this.$store.state.user.first_name} ${this.$store.state.user.last_name}`,
        staff_id: this.$store.state.user.staff_id
      };
    },

    convertInvoiceDatesFromUnix(invoice) {
      const convertUnixToDate = (unixTimestamp) => {
        return this.$moment.unix(unixTimestamp).format('DD-MM-YYYY');
      };

      // Convert invoice_date
      if (invoice.invoice_date) {
        invoice.invoice_date = convertUnixToDate(invoice.invoice_date);
      }

      // Convert payment_due_date
      if (invoice.payment_due_date) {
        invoice.payment_due_date = convertUnixToDate(invoice.payment_due_date);
      }

      return invoice;
    },

    async fetchDataMasterTaxCodes() {
      try {
        const response = await this.DatamasterService.getDataMasters('?noPaging=true&type=PO_Tax_Code&is_active=true&orderBy=name&order=1');
        this.tax_codes = response.map(item => ({
          data_master_id: item.data_master_id,
          name: item.name,
          value: item.value || '',
        }));
      } catch (error) {
        console.error('Error fetching data master certificates:', error);
      }
    },

    async formatInvoiceData(invoice) {
      let formattedInvoice = JSON.parse(JSON.stringify(invoice));

      // Formatting dates
      formattedInvoice = this.convertInvoiceDatesFromUnix(formattedInvoice);

      function findDefault(items) {
        return items && items.length > 0 ? items.find(item => item.is_default) || null : null;
      }

      // Handle sold to information
      if (formattedInvoice.sold_to_contact && formattedInvoice.sold_to_contact.contact_id && formattedInvoice.sold_to_contact.name) {
        this.sold_to = {
          name: formattedInvoice.sold_to_contact.name,
          contact_id: formattedInvoice.sold_to_contact.contact_id
        };

        this.sold_to.address = findDefault(formattedInvoice.sold_to_contact.addresses);

        this.sold_to.people = findDefault(formattedInvoice.sold_to_contact.people);
      }

      // Handle invoice to information
      if (formattedInvoice.invoice_to_contact && formattedInvoice.invoice_to_contact.contact_id && formattedInvoice.invoice_to_contact.name) {
        this.invoice_to = {
          name: formattedInvoice.invoice_to_contact.name,
          contact_id: formattedInvoice.invoice_to_contact.contact_id
        };

        this.invoice_to.address = findDefault(formattedInvoice.invoice_to_contact.addresses);

        this.invoice_to.people = findDefault(formattedInvoice.invoice_to_contact.people);
      }

      // Handle delivery to information
      if (formattedInvoice.delivery_to_contact && formattedInvoice.delivery_to_contact.contact_id && formattedInvoice.delivery_to_contact.name) {
        this.delivery_to = {
          name: formattedInvoice.delivery_to_contact.name,
          contact_id: formattedInvoice.delivery_to_contact.contact_id
        };

        this.delivery_to.address = findDefault(formattedInvoice.delivery_to_contact.addresses);

        this.delivery_to.people = findDefault(formattedInvoice.delivery_to_contact.people);
      }

      formattedInvoice.invoice_items = await this.convertToOrderItems(formattedInvoice.items);

      return formattedInvoice;
    },

    async convertToOrderItems(dbProducts) {

      if (!dbProducts || dbProducts.length === 0) {
        return [];
      }

      await this.fetchDataMasterTaxCodes();

      return dbProducts.map(product => {
        return {
          product_id: product.product_id,
          invoice_item_id: product.invoice_item_id,
          sales_item_id: product.sales_item_id,
          product_code: product.product_code,
          supplier_product_code: product.supplier_product_code,
          quantity: product.quantity.toString(),
          price: product.unit_price,
          tax_code: product.tax_code,
          tax_rate: product.tax_rate,
          total: product.total,
          total_with_tax: product.total_with_tax,
          description: product.description,
          tax_codes: this.tax_codes,
          received_items: product.received_items || []
        };
      });
    },

    async prefillSoldToFromQuery() {
      const contactId = this.$route.query.sold_to;
      if (!contactId) {
        return;
      }

      try {
        // Use the search endpoint (not getContactById) so the contact comes back in the
        // reduced { address, people } shape that ContactInfoCard and handleSoldToSelected expect —
        // matching what ContactAutocomplete provides on a manual selection.
        const results = await this.ContactService.searchContactsWithType({ contact_id: contactId });
        const contact = results && results.length > 0 ? results[0] : null;
        if (contact && contact.contact_id) {
          this.sold_to = contact;
          await this.handleSoldToSelected({ value: contact });
        }
      } catch (error) {
        console.error("Error prefilling contact from query:", error);
      }
    },

    handleSoldToSelected: async function (selectedCustomer) {
      // Access the actual data from the value prop of selectedCustomer
      const customerData = selectedCustomer.value;

      if (customerData && customerData.agent_id) {
        try {
          const response = await this.ContactService.searchContactsWithType({ contact_id: customerData.agent_id });

          // Fall back to SOLD TO if the assigned agent can't be resolved (e.g. stale agent_id)
          const assignedAgent = response && response.length > 0 ? response[0] : customerData;
          this.invoice_to = _.cloneDeep(assignedAgent);
        } catch (error) {
          console.error("Error fetching agent details: ", error);
          this.invoice_to = _.cloneDeep(customerData);
        }
      } else {
        // If SOLD TO customer has no assigned agent
        this.invoice_to = _.cloneDeep(customerData); // Set INVOICE TO same as SOLD TO
      }

      // DELIVERY TO always defaults to SOLD TO
      this.delivery_to = _.cloneDeep(customerData);
    },


    handleSoldToCleared(selectedContact) {
      this.sold_to = null;
      this.invoice_to = null;
      this.delivery_to = null;
    },

    handleInvoiceToSelected: function (selectedCustomer) {

    },

    handleInvoiceToCleared(selectedContact) {

    },

    handleDeliveryToSelected: function (selectedCustomer) {

    },

    handleDeliveryToCleared(selectedContact) {

    },


    async loadInvoiceByInvoiceId() {
      try {
        const invoiceData = await this.InvoiceService.getInvoiceById(this.invoice_id);

        if (!invoiceData) {
          return;
        }
        return invoiceData;

      } catch (error) {
        this.isBusy = false;
        console.error("Error loading invoice:", error);
      }
    },

    convertInvoiceDatesToUnix(invoice) {
      const convertDateToUnix = (date) => {
        if (date instanceof Date) {
          return Math.floor(date.getTime() / 1000);
        } else if (typeof date === 'string') {
          const momentDate = this.$moment(date, 'DD-MM-YYYY', true);
          if (momentDate.isValid()) {
            return momentDate.unix();
          } else {
            const isoMomentDate = this.$moment(date, this.$moment.ISO_8601);
            if (isoMomentDate.isValid()) {
              return isoMomentDate.unix();
            }
          }
        }
        console.error('Invalid or unsupported date format:', date);
        return null;
      };

      // Convert invoice_date
      if (invoice.invoice_date) {
        invoice.invoice_date = convertDateToUnix(invoice.invoice_date);
      }

      // Convert payment_due_date
      if (invoice.payment_due_date) {
        invoice.payment_due_date = convertDateToUnix(invoice.payment_due_date);
      }

      return invoice;
    },

    getInvoiceData(invoice) {

      let convertedInvoice = JSON.parse(JSON.stringify(invoice));

      // convertedPurchase.items = this.convertProductsForDB();
      convertedInvoice = this.convertInvoiceDatesToUnix(convertedInvoice);

      if (this.sold_to && this.sold_to.contact_id) {
        convertedInvoice.sold_to = this.sold_to.contact_id;
      }
      if (this.invoice_to && this.invoice_to.contact_id) {
        convertedInvoice.invoice_to = this.invoice_to.contact_id;
      }
      if (this.delivery_to && this.delivery_to.contact_id) {
        convertedInvoice.delivery_to = this.delivery_to.contact_id;
      }

      convertedInvoice.duty = parseFloat(this.invoice.duty) || 0;
      convertedInvoice.shipping_cost = parseFloat(this.invoice.shipping_cost) || 0;
      convertedInvoice.shipping_tax_code = this.invoice.shipping_tax_code;
      convertedInvoice.shipping_tax_rate = parseFloat(this.invoice.shipping_tax_rate) || 0;



      return convertedInvoice;
    },

    getOrderItems() {
      return this.$refs.orderItemRef ? this.$refs.orderItemRef.getCurrentProducts() : [];
    },

    closeViewer() {
      this.pdfViewerData = {
        pdfData: null,
        documentName: "",
        isShowingViewer: false,
      };
    },

    async previewInvoice() {
      if (!this.invoice_id) {
        this.notifyWarning("Missing invoice ID. Please ensure all required fields are filled.");
        return;
      }

      try {
        const response = await this.InvoiceService.generateInvoiceReport(this.invoice_id, 'invoice');

        this.pdfViewerData = {
          pdfData: response,
          documentName: 'Invoice Report',
          isShowingViewer: true,
        };
      } catch (error) {
        console.error('Error loading PDF:', error);
        this.notifyWarning("Failed to load the PDF document. Please try again.");
      }
    },

    async printInvoice() {
      if (!this.invoice_id) {
        this.notifyWarning("Missing invoice ID. Please ensure all required fields are filled.");
        return;
      }

      try {
        const response = await this.InvoiceService.generateInvoiceReport(this.invoice_id, 'invoice');

        this.pdfViewerData = {
          pdfData: response,
          documentName: 'Invoice Report',
          isShowingViewer: true,
        };
      } catch (error) {
        console.error('Error loading PDF:', error);
        this.notifyWarning("Failed to load the PDF document. Please try again.");
      }
    },

    validateRequiredFields() {
      const requiredFields = [
        { field: this.sold_to, name: 'Sold To' },
        { field: this.invoice_to, name: 'Invoice To' },
        { field: this.invoice.invoice_type, name: 'Invoice Type' },
        { field: this.invoice.invoice_date, name: 'Invoice Date' },
        { field: this.invoice.payment_due_date, name: 'Requested Delivery Date' },
      ];

      for (const field of requiredFields) {
        if (!field.field) {
          this.notifyError(`${field.name} is required.`);
          return false;
        }
      }
      return true;
    },


    saveInvoice: async function () {
      if (!this.validateRequiredFields()) {
        return false; // Return false if validation fails
      }

      this.isBusy = true;
      const formattedInvoice = this.getInvoiceData(this.invoice);
      try {
        if (this.invoice_id) {
          await this.InvoiceService.updateInvoice(this.invoice_id, formattedInvoice);
          this.notifySuccess("Invoice updated successfully");
          await this.loadInvoiceAndApply();
        } else {
          const response = await this.InvoiceService.createInvoice(formattedInvoice);
          this.invoice_id = response.invoice_id;
          await this.$router.push({
            name: 'invoice-edit',
            params: { invoice_id: this.invoice_id },
          });
          this.notifySuccess("Invoice created successfully");
        }
        return true; // Indicate success
      } catch (error) {
        const errorMessage = this.invoice_id
            ? "Couldn't update Invoice"
            : "Couldn't create Invoice";
        this.notifyError(errorMessage);
        console.error(error);
        return false; // Indicate failure
      } finally {
        this.isBusy = false;
      }
    },

    submitInvoice: async function (event) {
      event.preventDefault();
    },

    formatMoney(value) {
      const amount = parseFloat(value) || 0;
      const formatted = Math.abs(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return amount < 0 ? `- £${formatted}` : `£${formatted}`;
    },
    async updateInvoiceStatus(status) {
      const previousStatus = this.invoice.status;
      this.invoice.status = status;
      const saveSuccessful = await this.saveInvoice();
      if (!saveSuccessful) {
        this.invoice.status = previousStatus;
      }
    },

    async confirmMarkAsPaid() {
      const confirmed = await this.confirmAction({
        message: "Are you sure you want to mark this invoice as PAID? Once marked as paid, the invoice can no longer be edited.",
        header: "Confirm Mark As Paid",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (confirmed) {
        await this.updateInvoiceStatus("paid");
      }
    },

    async unmarkAsPaid() {
      const reason = await this.$refs.unmarkPaidConfirm.open();
      if (!reason) {
        return;
      }

      const previousStatus = this.invoice.status;
      const previousNotes = this.invoice.internal_notes;
      const noteEntry = `[Unmarked as paid on ${this.$moment().format('DD-MM-YYYY HH:mm')}] ${reason}`;
      this.invoice.internal_notes = previousNotes ? `${previousNotes}\n${noteEntry}` : noteEntry;
      this.invoice.status = "pending";

      const saveSuccessful = await this.saveInvoice();
      if (!saveSuccessful) {
        this.invoice.status = previousStatus;
        this.invoice.internal_notes = previousNotes;
      }
    },

    async convertToInvoice() {
      const confirmed = await this.confirmAction({
        message: `Convert pro-forma ${this.invoice.invoice_no} to an invoice? It will be issued a new invoice number, `
            + 'dated today with payment due in 30 days, and will count towards the customer\'s balance. '
            + 'The pro-forma number is kept as a reference.',
        header: 'Convert to Invoice',
        acceptLabel: 'Convert',
        rejectLabel: 'Cancel',
      });

      if (!confirmed) {
        return;
      }

      this.isBusy = true;
      try {
        const converted = await this.InvoiceService.convertProFormaToInvoice(this.invoice_id);
        this.notifySuccess(`Converted to invoice ${converted.invoice_no}`);
        await this.loadInvoiceAndApply();
      } catch (error) {
        const serverMessage = error && error.data && error.data.message;
        this.notifyError(serverMessage ? String(serverMessage) : "Couldn't convert the pro-forma to an invoice");
        console.error(error);
      } finally {
        this.isBusy = false;
      }
    },

    async openAdditionalCreditPanel() {
      this.isBusy = true;
      try {
        this.creditSummary = await this.CreditService.getInvoiceCreditSummary(this.invoice_id);
        this.additionalCreditPanelOpen = true;
      } catch (error) {
        const serverMessage = error && error.data && error.data.message;
        this.notifyError(serverMessage ? String(serverMessage) : "Couldn't load the credit notes for this invoice.");
      } finally {
        this.isBusy = false;
      }
    },

    openBlockingCredit() {
      const blocking = this.creditSummary && this.creditSummary.blocking_credit;
      if (blocking) {
        this.additionalCreditPanelOpen = false;
        this.$router.push({ name: "credit-edit", params: { credit_id: blocking.credit_id } });
      }
    },

    async createCreditNoteFromPanel() {
      const max = this.creditSummary ? this.formatMoney(this.creditSummary.max_amount) : "";
      const confirmed = await this.confirmAction({
        message: `Create another credit note for ${this.invoice.invoice_no}? It starts at the most that can still be credited (${max}) and can be reduced before it is issued.`,
        header: "Confirm Credit Creation",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmed) {
        return;
      }

      this.isBusy = true;
      try {
        const createdCredit = await this.CreditService.convertFromInvoice(this.invoice_id);
        this.additionalCreditPanelOpen = false;
        this.notifySuccess("Credit created successfully.");
        await this.$router.push({
          name: "credit-edit",
          params: { credit_id: createdCredit.credit_id },
        });
      } catch (error) {
        const serverMessage = error && error.data && error.data.message;
        this.notifyError(serverMessage ? String(serverMessage) : "An error occurred while creating credit. Please try again.");
      } finally {
        this.isBusy = false;
      }
    },

    async createCredit() {
      if (!this.invoice_id) {
        this.notifyError("Invoice ID is required to create a credit.");
        return;
      }

      // The invoice has been credited before: show what is already credited and how much more
      // can be credited, and let the user go on from there
      if (this.invoice.is_credit_created) {
        await this.openAdditionalCreditPanel();
        return;
      }

      try {
        const confirmation = await this.confirmAction({
          message: "Are you sure you want to create a credit for this invoice?",
          header: "Confirm Credit Creation",
          acceptLabel: "Yes",
          rejectLabel: "No",
        });

        if (!confirmation) {
          this.notifyInfo("Credit creation canceled.");
          return;
        }

        this.isBusy = true;

        // Call the credit creation API with the invoice ID
        const createdCredit = await this.CreditService.convertFromInvoice(this.invoice_id);

        // Redirect to the credit edit page if credit is created successfully
        if (createdCredit && createdCredit.credit_id) {
          this.notifySuccess("Credit created successfully.");
          await this.$router.push({
            name: "credit-edit",
            params: { credit_id: createdCredit.credit_id },
          });
        } else {
          this.notifyError("Failed to create credit. No response received.");
        }
      } catch (error) {
        console.error("Error creating credit:", error);
        this.notifyError("An error occurred while creating credit. Please try again.");
      } finally {
        this.isBusy = false;
      }
    },




  }
}
;
</script>

<style>
.hide-header .p-datatable-thead {
  display: none;
}

</style>

<style scoped>
.p-component-overlay {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton,
::v-deep .status-split-button .p-splitbutton-menubutton {
  background-color: #bee3f8 !important;
  border-color: #63b3ed !important;
  color: #383E42 !important;
  box-shadow: none !important;
  transition: all 300ms ease-in-out !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton:hover,
::v-deep .status-split-button .p-splitbutton-menubutton:hover {
  background-color: #90cdf4 !important;
  border-color: #4299e1 !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06) !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton:active,
::v-deep .status-split-button .p-splitbutton-menubutton:active {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06) !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton {
  border-top-left-radius: 0.375rem !important;
  border-bottom-left-radius: 0.375rem !important;
}

::v-deep .status-split-button .p-splitbutton-menubutton {
  border-top-right-radius: 0.375rem !important;
  border-bottom-right-radius: 0.375rem !important;
}
</style>